import dayjs from 'dayjs';
import { LockDto } from 'services/locks/locks.service';
import { CreateKeyInput } from 'services/keys/keys.service';

type Translate = (o: { key: string; extra?: Record<string, unknown> }) => string;

export type VigenciaPreset = 'always' | 'm1' | 'm3' | 'y1' | 'exact';

// Lo que el usuario decidió. Los campos de restricción existen siempre aunque
// estén plegados: plegar es esconder, no resetear.
export interface KeyDraft {
    lock: LockDto;
    assigneeEmail: string;
    preset: VigenciaPreset;
    startDate: string; // YYYY-MM-DD, sólo si preset === 'exact'
    endDate: string; // idem
    dow: number;
    startTime: string; // HH:mm
    endTime: string; // HH:mm
}

// 100 años × 365,25. No es un número redondo elegido acá: es la convención que
// ya usan 3044 llaves en producción, con vencimientos hasta 2175.
export const ALWAYS_DAYS = 36525;
export const ALL_DAYS = 127;

// El orden y los bits son los del backend (api/smart_token_record.go, var dow):
// m,t,w,r,f,s,u. Domingo es 64.
export const DAY_BITS: { labelKey: string; bit: number }[] = [
    { labelKey: 'key-access.day-mon', bit: 1 },
    { labelKey: 'key-access.day-tue', bit: 2 },
    { labelKey: 'key-access.day-wed', bit: 4 },
    { labelKey: 'key-access.day-thu', bit: 8 },
    { labelKey: 'key-access.day-fri', bit: 16 },
    { labelKey: 'key-access.day-sat', bit: 32 },
    { labelKey: 'key-access.day-sun', bit: 64 },
];

// LockDto.timezone NO es un offset ni un nombre IANA: es el ISO3 de país que
// devuelve el backend (api/lock.go, timeZones[...].ISO3).
const IANA_BY_ISO3: Record<string, string> = {
    ARG: 'America/Argentina/Buenos_Aires',
    DOM: 'America/Santo_Domingo',
    // El catálogo del backend dice "America/Peru/Lima", que no existe en la tz
    // database. Acá va el identificador válido.
    PER: 'America/Lima',
    COL: 'America/Bogota',
};

export function ianaFor(iso3: string): string | undefined {
    return IANA_BY_ISO3[iso3];
}

// 'en-CA' produce YYYY-MM-DD, que es justo el formato que espera el backend.
export function todayIn(iso3: string, now: Date = new Date()): string {
    const timeZone = ianaFor(iso3);
    return new Intl.DateTimeFormat('en-CA', timeZone ? { timeZone } : {}).format(now);
}

const untilFor = (preset: VigenciaPreset, since: string, endDate: string): string => {
    const from = dayjs(since);
    switch (preset) {
        case 'always':
            return from.add(ALWAYS_DAYS, 'day').format('YYYY-MM-DD');
        case 'm1':
            return from.add(1, 'month').format('YYYY-MM-DD');
        case 'm3':
            return from.add(3, 'month').format('YYYY-MM-DD');
        case 'y1':
            return from.add(1, 'year').format('YYYY-MM-DD');
        case 'exact':
            return endDate;
    }
};

export function buildKeyPayload(draft: KeyDraft, now: Date = new Date()): CreateKeyInput {
    const since = draft.preset === 'exact' ? draft.startDate : todayIn(draft.lock.timezone, now);
    return {
        lock_id: draft.lock.id,
        guest: { value: draft.assigneeEmail.trim(), type_id: 1 },
        since_date: since,
        until_date: untilFor(draft.preset, since, draft.endDate),
        since_time: draft.startTime,
        until_time: draft.endTime,
        dow: draft.dow,
    };
}

// El último día encendido no se puede apagar: con dow=0 la llave no abre nunca.
export function toggleDay(dow: number, bit: number): number {
    const next = dow & bit ? dow & ~bit : dow | bit;
    return next === 0 ? dow : next;
}

export function isDraftComplete(draft: KeyDraft): boolean {
    if (draft.startTime >= draft.endTime) {
        return false;
    }
    if (draft.preset !== 'exact') {
        return true;
    }
    return !!draft.startDate && !!draft.endDate && draft.startDate < draft.endDate;
}

export function buildKeyValidityLabel(draft: KeyDraft, translate: Translate, now: Date = new Date()): string {
    const parts: string[] = [];

    if (draft.preset === 'always') {
        parts.push(translate({ key: 'key-access.validity-always' }));
    } else if (draft.preset === 'exact') {
        parts.push(
            translate({ key: 'key-access.validity-range', extra: { from: draft.startDate, to: draft.endDate } }),
        );
    } else {
        const since = todayIn(draft.lock.timezone, now);
        parts.push(
            translate({
                key: 'key-access.validity-until',
                extra: { to: untilFor(draft.preset, since, draft.endDate) },
            }),
        );
    }

    if (draft.dow !== ALL_DAYS) {
        parts.push(
            draft.dow === 31
                ? translate({ key: 'key-access.days-weekdays' })
                : translate({
                      key: 'key-access.days-some',
                      extra: {
                          days: DAY_BITS.filter((d) => draft.dow & d.bit)
                              .map((d) => translate({ key: d.labelKey }))
                              .join(', '),
                      },
                  }),
        );
    }

    if (draft.startTime !== '00:00' || draft.endTime !== '23:59') {
        parts.push(translate({ key: 'key-access.hours', extra: { from: draft.startTime, to: draft.endTime } }));
    }

    return parts.join(', ');
}
